import React, { Component } from 'react';
import ReactDOM from 'react-dom';

import CheckoutLicenseWizard from '~/components/legacy/CheckoutLicenseWizard.jsx';
import { buildProjectLicenseConfigFamilies } from '~/components/legacy/projectLicenseConfigFamilies.js';
import { parseReactProps } from '~/components/legacy/reactProps.js';

const CHECKOUT_LICENSE_OPEN_EVENT = 'checkout-license-configurator:request-open';
const CHECKOUT_LICENSE_COMPLETED_EVENT = 'checkout-license-configurator:completed';
export const CHECKOUT_LICENSE_STATUS_CHANGED_EVENT = 'checkout-license-configurator:status-changed';
const CHECKOUT_LICENSE_COMPLETION_ADVANCE_DELAY_MS = 520;

let checkoutLicenseAccordionCoordinatorRegistered = false;

function registerCheckoutLicenseAccordionCoordinator() {
  if (checkoutLicenseAccordionCoordinatorRegistered || typeof window === 'undefined') {
    return;
  }

  window.addEventListener(CHECKOUT_LICENSE_COMPLETED_EVENT, (event) => {
    const sourceKey = event.detail?.sourceKey;
    if (!sourceKey || typeof window.CustomEvent !== 'function') {
      return;
    }

    window.setTimeout(() => {
      const items = Array.from(document.querySelectorAll('[data-checkout-license-key]'));
      const sourceIndex = items.findIndex((item) => item.dataset.checkoutLicenseKey === sourceKey);

      if (sourceIndex === -1) {
        return;
      }

      const nextItem = items.slice(sourceIndex + 1).find((item) => item.dataset.checkoutLicenseStatus !== 'completed');

      if (!nextItem?.dataset.checkoutLicenseKey) {
        return;
      }

      window.dispatchEvent(new window.CustomEvent(CHECKOUT_LICENSE_OPEN_EVENT, {
        detail: { targetKey: nextItem.dataset.checkoutLicenseKey }
      }));
    }, CHECKOUT_LICENSE_COMPLETION_ADVANCE_DELAY_MS);
  });

  checkoutLicenseAccordionCoordinatorRegistered = true;
}

class CheckoutProjectLicenseConfigurator extends Component {
  constructor(props) {
    super(props);

    this.handleEditorOpen = this.handleEditorOpen.bind(this);
    this.handleEditorClose = this.handleEditorClose.bind(this);
    this.handleConfigurationAdopt = this.handleConfigurationAdopt.bind(this);
    this.handleWizardChange = this.handleWizardChange.bind(this);
    this.handleExternalOpenRequest = this.handleExternalOpenRequest.bind(this);
    this.setPriceProxyRef = this.setPriceProxyRef.bind(this);
    this.csrfToken = this.csrfToken.bind(this);

    this.state = {
      mode: props.startInEditMode ? 'edit' : 'summary',
      editorSessionKey: 0,
      adoptedConfiguration: props.configuredSelection ? this.prefillConfigurationFromProps(props) : null,
      liveEditorConfiguration: null,
      completionFlashActive: false,
      editorOriginStatus: props.configuredSelection ? 'completed' : props.summaryDisplayState || 'active_open',
      persistError: null,
      persistState: 'idle'
    };
  }

  componentDidMount() {
    registerCheckoutLicenseAccordionCoordinator();
    if (typeof window !== 'undefined') {
      window.addEventListener(CHECKOUT_LICENSE_OPEN_EVENT, this.handleExternalOpenRequest);
    }

    this.syncPriceInfo();
    this.dispatchStatusChangeEvent();

    if (this.props.startInEditMode) {
      this.dispatchOpenRequest();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const priceChanged = this.currentPriceCents(prevProps, prevState) !== this.currentPriceCents();
    const selectionChanged = JSON.stringify(this.currentSelectedOptionKeys(prevProps, prevState)) !== JSON.stringify(this.currentSelectedOptionKeys());
    const previousSummaryDisplayState = this.summaryDisplayState(prevProps, prevState);
    const currentSummaryDisplayState = this.summaryDisplayState();

    if (priceChanged || selectionChanged) {
      this.syncPriceInfo();
    }

    if (previousSummaryDisplayState !== currentSummaryDisplayState) {
      this.dispatchStatusChangeEvent();
    }
  }

  componentWillUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener(CHECKOUT_LICENSE_OPEN_EVENT, this.handleExternalOpenRequest);
    }

    if (this.completionFlashTimeout) {
      window.clearTimeout(this.completionFlashTimeout);
    }
  }

  setPriceProxyRef(element) {
    this.priceProxyRef = element;
  }

  syncPriceInfo() {
    if (!this.priceProxyRef || typeof Event !== 'function') {
      return;
    }

    this.priceProxyRef.dispatchEvent(new Event('change', { bubbles: true }));
  }

  componentKey(props = this.props) {
    if (props.cartItemId) {
      return `cart-item-${props.cartItemId}`;
    }

    if (props.usageLicenseId) {
      return `usage-license-${props.usageLicenseId}`;
    }

    return `track-${props.trackId}-${props.fieldPrefix}`;
  }

  dispatchOpenRequest() {
    if (typeof window === 'undefined' || typeof window.CustomEvent !== 'function') {
      return;
    }

    window.dispatchEvent(new window.CustomEvent(CHECKOUT_LICENSE_OPEN_EVENT, {
      detail: { targetKey: this.componentKey() }
    }));
  }

  dispatchStatusChangeEvent(status = this.summaryDisplayState()) {
    if (typeof window === 'undefined' || typeof window.CustomEvent !== 'function') {
      return;
    }

    window.dispatchEvent(new window.CustomEvent(CHECKOUT_LICENSE_STATUS_CHANGED_EVENT, {
      detail: {
        sourceKey: this.componentKey(),
        status
      }
    }));
  }

  handleExternalOpenRequest(event) {
    const targetKey = event.detail?.targetKey;

    if (!targetKey) {
      return;
    }

    if (targetKey === this.componentKey()) {
      if (this.state.mode !== 'edit') {
        this.openEditor(this.summaryDisplayState());
      }

      return;
    }

    if (this.state.mode === 'edit') {
      this.setState({ mode: 'summary' });
    }
  }

  openEditor(originStatus = this.summaryDisplayState()) {
    const nextState = {
      mode: 'edit',
      persistError: null,
      editorOriginStatus: originStatus
    };

    if (this.state.mode !== 'edit') {
      nextState.editorSessionKey = this.state.editorSessionKey + 1;
    }

    this.setState(nextState);
  }

  triggerCompletionFlash() {
    if (this.completionFlashTimeout && typeof window !== 'undefined') {
      window.clearTimeout(this.completionFlashTimeout);
    }

    this.setState({ completionFlashActive: true });

    if (typeof window !== 'undefined') {
      this.completionFlashTimeout = window.setTimeout(() => {
        this.setState({ completionFlashActive: false });
      }, 1200);
    }
  }

  normalizeOptionKeys(optionKeys) {
    return Array(optionKeys)
      .flatMap((value) => String(value || '').split(','))
      .map((value) => value.trim())
      .filter(Boolean);
  }

  resolvedFamily(props = this.props) {
    if (props.family) {
      return props.family;
    }

    const familyKey = props.usageLicenseTypeKey || props.familyKey;
    if (!familyKey) {
      return null;
    }

    const globalFamilies =
      typeof window !== 'undefined' && Array.isArray(window.__projectLicenseConfigFamilies) ?
        window.__projectLicenseConfigFamilies :
        [];
    const fallbackFamilies = globalFamilies.length > 0 ? globalFamilies : buildProjectLicenseConfigFamilies(props.locale);

    return fallbackFamilies.find((family) => family.key === familyKey) || null;
  }

  optionQuestionMap(family) {
    return (family?.questions || []).reduce((map, question) => {
      question.options.forEach((option) => {
        map[option.key] = question.key;
      });

      return map;
    }, {});
  }

  answersForOptionKeys(optionKeys, family = this.resolvedFamily(), fallbackOptionKeys = this.props.defaultOptionKeys) {
    const questionMap = this.optionQuestionMap(family);
    const answers = {};

    this.normalizeOptionKeys(fallbackOptionKeys).forEach((optionKey) => {
      const questionKey = questionMap[optionKey];

      if (questionKey && !answers[questionKey]) {
        answers[questionKey] = optionKey;
      }
    });

    this.normalizeOptionKeys(optionKeys).forEach((optionKey) => {
      const questionKey = questionMap[optionKey];

      if (questionKey) {
        answers[questionKey] = optionKey;
      }
    });

    return answers;
  }

  completeConfiguredAnswers(rawAnswers = {}, family = this.resolvedFamily()) {
    const answers = { ...(rawAnswers || {}) };

    (family?.questions || []).forEach((question) => {
      if (!answers[question.key] && question.options[0]?.key) {
        answers[question.key] = question.options[0].key;
      }
    });

    return answers;
  }

  persistableOptionKeys(optionKeys, props = this.props) {
    const normalizedOptionKeys = this.normalizeOptionKeys(optionKeys);
    const allowedOptionKeys = new Set(this.normalizeOptionKeys(props.allowedOptionKeys));

    if (allowedOptionKeys.size === 0) {
      return normalizedOptionKeys;
    }

    return normalizedOptionKeys.filter((optionKey) => allowedOptionKeys.has(optionKey));
  }

  prefillConfigurationFromProps(props = this.props) {
    const family = this.resolvedFamily(props);
    const normalizedOptionKeys = this.normalizeOptionKeys(props.selectedOptionKeys);

    if (normalizedOptionKeys.length === 0) {
      return null;
    }

    return {
      familyKey: family?.key,
      familyTitle: family?.title,
      selectedOptionKeys: this.persistableOptionKeys(normalizedOptionKeys, props),
      answers: this.completeConfiguredAnswers(
        this.answersForOptionKeys(normalizedOptionKeys, family, props.defaultOptionKeys),
        family
      ),
      estimatedPriceCents: props.initialPriceCents
    };
  }

  currentConfiguration(props = this.props, state = this.state) {
    if (state.adoptedConfiguration) {
      return state.adoptedConfiguration;
    }

    if (props.configuredSelection) {
      return this.prefillConfigurationFromProps(props);
    }

    return null;
  }

  currentSelectedOptionKeys(props = this.props, state = this.state) {
    const configuration = this.currentConfiguration(props, state);

    if (configuration) {
      return this.normalizeOptionKeys(configuration.selectedOptionKeys);
    }

    return [];
  }

  currentPriceCents(props = this.props, state = this.state) {
    const configuration = this.currentConfiguration(props, state);

    if (configuration?.estimatedPriceCents) {
      return configuration.estimatedPriceCents;
    }

    return props.initialPriceCents || 0;
  }

  currentNetPriceCents() {
    const grossPriceCents = this.currentGrossPriceCents();
    const vatRate = parseFloat(this.props.vatRate || 0);

    if (vatRate <= 0) {
      return grossPriceCents;
    }

    return Math.round(grossPriceCents / (1 + vatRate));
  }

  formatPrice(cents) {
    return new Intl.NumberFormat(this.props.locale === 'de' ? 'de-DE' : 'en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format((cents || 0) / 100);
  }

  eurString(cents) {
    return ((cents || 0) / 100).toFixed(2);
  }

  i18nValue(key) {
    if (this.props.i18n && this.props.i18n[key] !== undefined) {
      return this.props.i18n[key];
    }

    return CheckoutProjectLicenseConfigurator.defaultProps.i18n[key];
  }

  wizardI18n() {
    return {
      ...CheckoutProjectLicenseConfigurator.defaultProps.wizardI18n,
      ...(this.props.wizardI18n || {})
    };
  }

  csrfToken() {
    if (typeof document === 'undefined') {
      return '';
    }

    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
  }

  currentVatCents() {
    return this.currentGrossPriceCents() - this.currentNetPriceCents();
  }

  currentGrossPriceCents() {
    return this.currentPriceCents();
  }

  currentEditorPriceCents() {
    if (this.state.liveEditorConfiguration?.estimatedPriceCents !== undefined) {
      return this.state.liveEditorConfiguration.estimatedPriceCents;
    }

    return this.currentPriceCents();
  }

  currentEditorPriceLabel() {
    return this.i18nValue('editorCurrentPriceLabel');
  }

  optionMeta(optionKey) {
    const family = this.resolvedFamily();

    for (const question of family?.questions || []) {
      const option = question.options.find((entry) => entry.key === optionKey);

      if (option) {
        return { question, option };
      }
    }

    return null;
  }

  summaryOptionPairsForKeys(optionKeys) {
    const normalizedDefaultKeys = this.normalizeOptionKeys(this.props.defaultOptionKeys);
    let summaryKeys = this.normalizeOptionKeys(optionKeys).filter((optionKey) => !normalizedDefaultKeys.includes(optionKey));

    if (summaryKeys.length === 0) {
      summaryKeys = this.normalizeOptionKeys(optionKeys);
    }

    return summaryKeys
      .map((optionKey) => {
        const meta = this.optionMeta(optionKey);

        if (!meta) {
          return null;
        }

        return {
          key: optionKey,
          label: meta.question.summary_title || meta.question.title,
          value: meta.option.summary || meta.option.label
        };
      })
      .filter(Boolean)
      .slice(0, 4);
  }

  compactCompletedOptionLabel(optionKey, meta = this.optionMeta(optionKey)) {
    if (meta?.option?.short_name) {
      return meta.option.short_name;
    }

    const explicitLabels = {
      usage_relation_client_channels: 'Kundenprojekt',
      usage_relation_collab_on_own_channels: 'Kooperation',
      channel_scope_multi_channel: 'Mehrere Kanäle',
      paid_budget_tier_250: 'Paid bis 250 €',
      paid_budget_tier_2000: 'Paid bis 2.000 €',
      paid_budget_tier_10000: 'Paid bis 10.000 €',
      paid_budget_tier_50000: 'Paid bis 50.000 €',
      marketing_intent_promotional: 'Marketingfokus',
      monetization_yes: 'Monetarisierung',
      monetization_no: 'Ohne Monetarisierung',
      content_model_paid_product: 'Bezahltes Angebot',
      format_branding_scope_series_intro_outro: 'Intro / Outro',
      format_branding_scope_signature_branding: 'Branding',
      usage_intensity_multi_use: 'Mehrfachnutzung',
      usage_intensity_single_use: 'Einmalnutzung',
      territory_scope_dach: 'DACH',
      territory_scope_europe: 'Europa',
      territory_scope_global: 'Weltweit',
      rights_term_3m: '3 Monate',
      rights_term_6m: '6 Monate',
      rights_term_12m: '12 Monate',
      rights_module_vod_ott: 'VOD / OTT',
      rights_module_film_trailer: 'Trailer-Rechte'
    };

    if (explicitLabels[optionKey]) {
      return explicitLabels[optionKey];
    }

    if (!meta) {
      return optionKey;
    }

    const label = meta.option.label || meta.option.summary || optionKey;

    return label
      .replace(/^Ja,\s*/i, '')
      .replace(/^Nein,\s*/i, '')
      .replace(/^Für\s+/i, '')
      .replace(/^Auf\s+/i, '')
      .trim();
  }

  compactBaseTagLabel() {
    const familyTitle = (this.resolvedFamily()?.title || '').trim();
    const normalizedTitle = familyTitle.replace(/\s+Lizenz$/i, '').trim();

    if (!normalizedTitle) {
      return null;
    }

    return `${normalizedTitle}-Basis`;
  }

  completedTagItems() {
    const family = this.resolvedFamily();
    const baseTag = {
      key: 'base',
      label: this.compactBaseTagLabel(),
      variant: 'base'
    };

    let optionKeys = [];

    if (this.state.adoptedConfiguration) {
      optionKeys = this.normalizeOptionKeys(this.state.adoptedConfiguration.selectedOptionKeys);
    } else if (this.props.configuredSelection && family) {
      optionKeys = this.normalizeOptionKeys(this.props.selectedOptionKeys);
    }

    const normalizedDefaultKeys = this.normalizeOptionKeys(this.props.defaultOptionKeys);
    let selectedUpgradeKeys = optionKeys.filter((optionKey) => !normalizedDefaultKeys.includes(optionKey));

    if (selectedUpgradeKeys.length === 0) {
      selectedUpgradeKeys = optionKeys.filter((optionKey) => optionKey !== normalizedDefaultKeys[0]);
    }

    const tags = selectedUpgradeKeys
      .map((optionKey) => {
        const meta = this.optionMeta(optionKey);

        if (!meta) {
          return null;
        }

        const isUpgrade = meta.option.price_delta_cents > 0 || meta.option.price_multiplier > 1;

        return {
          key: optionKey,
          label: isUpgrade ? `+ ${this.compactCompletedOptionLabel(optionKey, meta)}` : this.compactCompletedOptionLabel(optionKey, meta),
          variant: isUpgrade ? 'upgrade' : 'detail'
        };
      })
      .filter(Boolean)
      .slice(0, 4);

    return [baseTag, ...tags].filter((tag) => Boolean(tag.label));
  }

  summaryPriceLabel(summaryDisplayState) {
    return this.i18nValue(`${summaryDisplayState}PriceLabel`);
  }

  summaryDisplayState(props = this.props, state = this.state) {
    if (this.currentConfiguration(props, state)) {
      return 'completed';
    }

    return props.summaryDisplayState || 'active_open';
  }

  familyAnswersByKey(configuration = this.currentConfiguration()) {
    const family = this.resolvedFamily();

    if (!configuration?.answers || !family?.key) {
      return {};
    }

    return {
      [family.key]: configuration.answers
    };
  }

  handleEditorOpen(event) {
    if (event && typeof event.preventDefault === 'function') {
      event.preventDefault();
    }

    const originStatus = this.summaryDisplayState();

    this.openEditor(originStatus);
    this.dispatchOpenRequest();
  }

  handleEditorClose(event) {
    if (event && typeof event.preventDefault === 'function') {
      event.preventDefault();
    }

    this.setState({
      mode: 'summary',
      liveEditorConfiguration: null
    });
  }

  handleWizardChange(configuration) {
    this.setState({
      liveEditorConfiguration: {
        familyKey: configuration.familyKey || this.resolvedFamily()?.key,
        familyTitle: configuration.familyTitle || this.resolvedFamily()?.title,
        answers: configuration.answers || {},
        selectedOptionKeys: this.normalizeOptionKeys(configuration.selectedOptionKeys),
        estimatedPriceCents: configuration.estimatedPriceCents
      }
    });
  }

  async handleConfigurationAdopt(configuration) {
    const shouldAutoAdvanceToNext = this.state.editorOriginStatus !== 'completed';
    const family = this.resolvedFamily();
    const normalizedSelectedOptionKeys = this.normalizeOptionKeys(configuration.selectedOptionKeys);
    const persistedOptionKeys = this.persistableOptionKeys(normalizedSelectedOptionKeys);
    const completedAnswers = this.completeConfiguredAnswers(configuration.answers || {}, family);
    const normalizedConfiguration = {
      familyKey: configuration.familyKey,
      familyTitle: configuration.familyTitle,
      selectedOptionKeys: persistedOptionKeys,
      answers: completedAnswers,
      estimatedPriceCents: configuration.estimatedPriceCents
    };

    this.setState({
      persistError: null,
      persistState: 'saving'
    });

    try {
      const response = await fetch(this.props.persistPath || '/c/checkouts/persist_project_license_selection', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken(),
          Accept: 'application/json'
        },
        body: JSON.stringify({
          cart_item_id: this.props.cartItemId,
          usage_license_id: this.props.usageLicenseId,
          usage_license_type_id: this.props.usageLicenseTypeId,
          project_license_actor_context: this.props.actorContext,
          project_license_option_keys: persistedOptionKeys
        })
      });

      if (!response.ok) {
        throw new Error(`Persist failed with status ${response.status}`);
      }

      const result = await response.json();

      this.setState({
        mode: 'summary',
        liveEditorConfiguration: null,
        persistError: null,
        persistState: 'saved',
        adoptedConfiguration: {
          ...normalizedConfiguration,
          selectedOptionKeys: this.persistableOptionKeys(result.project_license_option_keys || normalizedConfiguration.selectedOptionKeys),
          estimatedPriceCents: result.project_license_pricing_snapshot?.country_gross_price ?
            Math.round(parseFloat(result.project_license_pricing_snapshot.country_gross_price) * 100) :
            result.project_license_pricing_snapshot?.gross_price_eur ?
              Math.round(parseFloat(result.project_license_pricing_snapshot.gross_price_eur) * 100) :
            normalizedConfiguration.estimatedPriceCents
        }
      });

      this.triggerCompletionFlash();

      if (shouldAutoAdvanceToNext && typeof window !== 'undefined' && typeof window.CustomEvent === 'function') {
        window.dispatchEvent(new window.CustomEvent(CHECKOUT_LICENSE_COMPLETED_EVENT, {
          detail: { sourceKey: this.componentKey() }
        }));
      }
    } catch (_error) {
      this.setState({
        persistError: this.i18nValue('persistError'),
        persistState: 'idle'
      });
    }
  }

  renderHiddenFields() {
    const family = this.resolvedFamily();

    return (
      <>
        <select
          className='project-license-checkout-v2__price-proxy'
          data-action='initPriceInfoSelect'
          aria-hidden='true'
          hidden
          name={`${this.props.fieldPrefix}[usage_license_type_id]`}
          ref={this.setPriceProxyRef}
          style={{ display: 'none' }}
          defaultValue={String(this.props.usageLicenseTypeId)}
          tabIndex='-1'
        >
          <option
            data-price={this.eurString(this.currentNetPriceCents())}
            data-vat-price={this.eurString(this.currentVatCents())}
            data-gross-price={this.eurString(this.currentGrossPriceCents())}
            value={this.props.usageLicenseTypeId}
          >
            {`${family?.title || ''} (${this.formatPrice(this.currentPriceCents())})`}
          </option>
        </select>
        <input type='hidden' name={`${this.props.fieldPrefix}[track_id]`} value={this.props.trackId} />
        {this.props.usageLicenseId ? (
          <input type='hidden' name={`${this.props.fieldPrefix}[id]`} value={this.props.usageLicenseId} />
        ) : null}
        {this.props.actorContext ? (
          <input type='hidden' name={`${this.props.fieldPrefix}[project_license_actor_context]`} value={this.props.actorContext} />
        ) : null}
        {this.currentConfiguration() ? (
          <input type='hidden' name={`${this.props.fieldPrefix}[project_license_selection_confirmed]`} value='1' />
        ) : null}
        {this.currentSelectedOptionKeys().map((optionKey) => (
          <input
            key={`project-license-option-${optionKey}`}
            type='hidden'
            name={`${this.props.fieldPrefix}[project_license_option_keys][]`}
            value={optionKey}
          />
        ))}
      </>
    );
  }

  renderCompletedTags(tagItems) {
    if (tagItems.length === 0) {
      return null;
    }

    return (
      <ul className='project-license-checkout-v2__summary-tags'>
        {tagItems.map((item) => (
          <li
            className={`project-license-checkout-v2__summary-tag project-license-checkout-v2__summary-tag--${item.variant}`}
            key={item.key}
          >
            <span className='project-license-checkout-v2__summary-tag-label'>{item.label}</span>
          </li>
        ))}
      </ul>
    );
  }

  trackTitle() {
    return this.props.trackTitle || '';
  }

  trackFamilyLabel() {
    const familyTitle = (this.resolvedFamily()?.title || '').trim();

    if (!familyTitle) {
      return '';
    }

    if (/\s+lizenz$/i.test(familyTitle)) {
      return familyTitle.replace(/\s+lizenz$/i, '-Lizenz');
    }

    return /-lizenz$/i.test(familyTitle) ? familyTitle : `${familyTitle}-Lizenz`;
  }

  renderTrackHeader() {
    const familyLabel = this.trackFamilyLabel();

    return (
      <div className='project-license-checkout-v2__summary-track-meta'>
        {this.renderPlayAction()}
        <div className='project-license-checkout-v2__summary-track-copy'>
          <div className='checkout-license-card__track-title project-license-checkout-v2__summary-track-title'>
            <span className='project-license-checkout-v2__summary-track-title-text'>{this.trackTitle()}</span>
            {familyLabel ? (
              <span className='project-license-checkout-v2__summary-track-family'>{` (${familyLabel})`}</span>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  playActionProps() {
    if (!this.props.playTrackId) {
      return null;
    }

    return {
      href: '#',
      title: this.i18nValue('playTrackAction'),
      'aria-label': this.i18nValue('playTrackAction'),
      'data-action': 'playTrack',
      'data-play-track-id': this.props.playTrackId
    };
  }

  removeActionProps() {
    if (!this.props.removeHref && !this.props.removeAction) {
      return null;
    }

    return {
      href: this.props.removeHref || '#',
      title: this.i18nValue('removeFromCartAction'),
      'aria-label': this.i18nValue('removeFromCartAction'),
      'data-method': this.props.removeMethod || undefined,
      'data-action': this.props.removeAction || undefined,
      'data-checkout-id': this.props.removeCheckoutId || undefined,
      'data-usage-license-id': this.props.removeUsageLicenseId || undefined,
      'data-confirm': this.props.removeConfirm || undefined
    };
  }

  renderPlayAction() {
    const playProps = this.playActionProps();

    if (!playProps) {
      return null;
    }

    return (
      <a className='checkout-license-card__icon-button checkout-license-card__icon-button--play checkout-license-card__icon-button--inline' {...playProps}>
        <i className='fa fa-play' aria-hidden='true'></i>
      </a>
    );
  }

  renderRemoveAction() {
    const removeProps = this.removeActionProps();

    if (!removeProps) {
      return null;
    }

    return (
      <a className='project-license-checkout-v2__summary-remove-link text-grey' {...removeProps}>
        {this.i18nValue('removeFromCartAction')}
      </a>
    );
  }

  renderStatusBadge(summaryDisplayState) {
    const badgeClassNames = {
      active_open: 'active',
      queued_open: 'queued',
      completed: 'completed'
    };

    return (
      <span className={`project-license-checkout-v2__summary-kicker-badge project-license-checkout-v2__summary-kicker-badge--${badgeClassNames[summaryDisplayState]}`}>
        {this.i18nValue(`${summaryDisplayState}Status`)}
      </span>
    );
  }

  summaryActionLabel(summaryDisplayState) {
    if (summaryDisplayState === 'completed') {
      return this.i18nValue('adjustAction');
    }

    return this.i18nValue(`${summaryDisplayState}Action`);
  }

  renderSummaryAction(summaryDisplayState) {
    const isCompleted = summaryDisplayState === 'completed';
    const className = isCompleted ?
      'project-license-checkout-v2__summary-link-button project-license-checkout-v2__summary-link-button--accordion project-license-checkout-v2__summary-link-button--edit' :
      `project-license-checkout-v2__summary-button ${summaryDisplayState === 'active_open' ? 'project-license-checkout-v2__summary-button--primary' : 'project-license-checkout-v2__summary-button--queued'}`;

    return (
      <button
        type='button'
        className={className}
        onClick={this.handleEditorOpen}
      >
        {this.summaryActionLabel(summaryDisplayState)}
        <span className='project-license-checkout-v2__summary-action-chevron'>›</span>
      </button>
    );
  }

  renderBackToSummaryAction() {
    return (
      <button
        type='button'
        className='project-license-checkout-v2__summary-link-button project-license-checkout-v2__summary-link-button--accordion'
        onClick={this.handleEditorClose}
      >
        {this.i18nValue('backToSummary')}
        <span className='project-license-checkout-v2__summary-action-chevron'>›</span>
      </button>
    );
  }

  renderSummaryPanel(summaryDisplayState = this.summaryDisplayState()) {
    const family = this.resolvedFamily();
    const tagItems = this.completedTagItems();
    const completed = summaryDisplayState === 'completed';
    const showSummaryText = !completed;
    const shellClassNames = [
      'project-license-checkout-v2__summary-shell',
      `project-license-checkout-v2__summary-shell--${summaryDisplayState.replace('_', '-')}`,
      this.state.completionFlashActive ? 'project-license-checkout-v2__summary-shell--completion-flash' : null
    ].filter(Boolean).join(' ');
    const panelClassNames = [
      'project-license-checkout-v2__summary-panel',
      `project-license-checkout-v2__summary-panel--${summaryDisplayState.replace('_', '-')}`
    ].join(' ');
    const priceStackClassNames = [
      'project-license-checkout-v2__summary-price-stack',
      completed ? 'project-license-checkout-v2__summary-price-stack--completed' : null,
      summaryDisplayState === 'queued_open' ? 'project-license-checkout-v2__summary-price-stack--queued' : null
    ].filter(Boolean).join(' ');
    const removeAction = this.renderRemoveAction();
    const inlineSummaryAction = null;
    const sideSummaryAction = completed ? null : this.renderSummaryAction(summaryDisplayState);
    const detailSummaryAction = completed ? this.renderSummaryAction(summaryDisplayState) : null;
    const inlineRemoveAction = completed ? removeAction : null;
    const sideRemoveAction = completed ? null : removeAction;

    return (
      <div className={shellClassNames}>
        <div className={panelClassNames}>
          <div className='project-license-checkout-v2__summary-row'>
            <div className='project-license-checkout-v2__summary-main'>
              {this.renderTrackHeader()}
              <div className='project-license-checkout-v2__summary-title-row project-license-checkout-v2__summary-title-row--accordion'>
                {this.renderStatusBadge(summaryDisplayState)}
                {inlineRemoveAction ? (
                  <span className='project-license-checkout-v2__summary-inline-action'>
                    {inlineRemoveAction}
                  </span>
                ) : null}
              </div>
              {showSummaryText ? (
                <p className={`project-license-checkout-v2__summary-text project-license-checkout-v2__summary-text--${summaryDisplayState.replace('_', '-')}`}>
                  {this.i18nValue(`${summaryDisplayState}Text`)}
                </p>
              ) : null}
            </div>
            <div className='project-license-checkout-v2__summary-side'>
              <div className={priceStackClassNames}>
                <span className='project-license-checkout-v2__summary-price-label'>{this.summaryPriceLabel(summaryDisplayState)}</span>
                <div className='project-license-checkout-v2__summary-price'>{this.formatPrice(this.currentPriceCents())}</div>
              </div>
              {sideSummaryAction ? (
                <div className='project-license-checkout-v2__summary-actions project-license-checkout-v2__summary-actions--accordion'>
                  {sideSummaryAction}
                </div>
              ) : null}
            </div>
          </div>
          {completed || sideRemoveAction ? (
            <div className='project-license-checkout-v2__summary-detail-group'>
              <div className='project-license-checkout-v2__summary-detail-layout'>
                <div className='project-license-checkout-v2__summary-detail-main'>
                  {completed ? (
                    <div className='project-license-checkout-v2__summary-detail-inline'>
                      {tagItems.length > 0 ? this.renderCompletedTags(tagItems) : null}
                      {detailSummaryAction ? (
                        <span className='project-license-checkout-v2__summary-detail-inline-action'>
                          {detailSummaryAction}
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                {sideRemoveAction ? (
                  <div className='project-license-checkout-v2__summary-detail-side'>
                    {sideRemoveAction}
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  renderSummary() {
    return this.renderSummaryPanel(this.summaryDisplayState());
  }

  renderEditor() {
    const family = this.resolvedFamily();
    const initialConfiguration = this.currentConfiguration();
    const initialAnswers = initialConfiguration?.answers || {};
    const summaryDisplayState = this.summaryDisplayState();
    const priceStackClassNames = [
      'project-license-checkout-v2__summary-price-stack',
      summaryDisplayState === 'completed' ? 'project-license-checkout-v2__summary-price-stack--completed' : null,
      summaryDisplayState === 'queued_open' ? 'project-license-checkout-v2__summary-price-stack--queued' : null
    ].filter(Boolean).join(' ');

    return (
      <div className='project-license-checkout-v2__editor project-license-checkout-v2__editor--wizard'>
        <div className='project-license-checkout-v2__editor-head'>
          <div className='project-license-checkout-v2__summary-row'>
            <div className='project-license-checkout-v2__summary-main project-license-checkout-v2__editor-context'>
              {this.renderTrackHeader()}
              <div className='project-license-checkout-v2__summary-title-row project-license-checkout-v2__summary-title-row--accordion'>
                {this.renderStatusBadge(summaryDisplayState)}
              </div>
            </div>
            <div className='project-license-checkout-v2__summary-side'>
              <div className={priceStackClassNames}>
                <span className='project-license-checkout-v2__summary-price-label'>{this.currentEditorPriceLabel()}</span>
                <div className='project-license-checkout-v2__summary-price'>{this.formatPrice(this.currentEditorPriceCents())}</div>
              </div>
            </div>
          </div>
        </div>
        {this.props.errors.length > 0 ? (
          <div className='alert alert-danger margin-bottom10'>
            {this.props.errors.join(', ')}
          </div>
        ) : null}
        {this.state.persistError ? (
          <div className='alert alert-danger margin-bottom10'>
            {this.state.persistError}
          </div>
        ) : null}
        <CheckoutLicenseWizard
          key={`checkout-license-wizard-${this.state.editorSessionKey}`}
          locale={this.props.locale}
          family={family}
          initialAnswers={initialAnswers}
          initialWizardStepIndex={0}
          onSubmit={this.handleConfigurationAdopt}
          onWizardChange={this.handleWizardChange}
          i18n={this.wizardI18n()}
        />
        <div className='project-license-checkout-v2__editor-footer'>
          {this.renderBackToSummaryAction()}
        </div>
      </div>
    );
  }

  render() {
    if (!this.resolvedFamily() || !this.props.usageLicenseTypeId) {
      return null;
    }

    const summaryDisplayState = this.summaryDisplayState();
    const rootClassNames = [
      'project-license-checkout-v2__react',
      `project-license-checkout-v2__react--${summaryDisplayState.replace('_', '-')}`,
      this.state.mode === 'edit' ? 'project-license-checkout-v2__react--editing' : 'project-license-checkout-v2__react--summary'
    ].join(' ');

    return (
      <div
        className={rootClassNames}
        data-checkout-license-key={this.componentKey()}
        data-checkout-license-status={summaryDisplayState}
        data-checkout-license-mode={this.state.mode}
      >
        {this.renderHiddenFields()}
        {this.state.mode === 'edit' ? this.renderEditor() : this.renderSummary()}
      </div>
    );
  }
}

export function parseCheckoutProjectLicenseConfiguratorProps(rawProps) {
  return parseReactProps(rawProps);
}

CheckoutProjectLicenseConfigurator.defaultProps = {
  locale: 'de',
  family: null,
  displayPosition: null,
  trackTitle: '',
  trackId: null,
  playTrackId: null,
  removeHref: null,
  removeMethod: null,
  removeAction: null,
  removeCheckoutId: null,
  removeUsageLicenseId: null,
  removeConfirm: null,
  usageLicenseId: null,
  cartItemId: null,
  usageLicenseTypeId: null,
  usageLicenseTypeKey: null,
  persistPath: '/c/checkouts/persist_project_license_selection',
  actorContext: null,
  fieldPrefix: 'checkout[usage_licenses_attributes][0]',
  configuredSelection: false,
  selectedOptionKeys: [],
  defaultOptionKeys: [],
  allowedOptionKeys: [],
  initialPriceCents: 0,
  initialVatCents: 0,
  initialGrossPriceCents: 0,
  vatRate: 0,
  summaryOptionLabels: [],
  summaryDisplayState: null,
  startInEditMode: false,
  errors: [],
  i18n: {
    active_openStatus: 'Noch nicht',
    queued_openStatus: 'Danach',
    completedStatus: 'Fertig konfiguriert',
    active_openText: 'Beantworten Sie jetzt noch kurz die Fragen zu Ihrem Projekt.',
    queued_openText: 'Diese Lizenz folgt direkt im Anschluss.',
    active_openPriceLabel: 'Startpreis',
    queued_openPriceLabel: 'Startpreis',
    completedPriceLabel: 'Preis',
    active_openAction: 'Konfigurieren',
    queued_openAction: 'Konfigurieren',
    adjustAction: 'Bearbeiten',
    backToSummary: 'Konfiguration abbrechen',
    playTrackAction: 'Track anhören',
    removeFromCartAction: 'Aus Warenkorb entfernen',
    familyEyebrow: 'Ausgewählte Lizenzfamilie',
    editorStartPriceLabel: 'Startpreis',
    editorCurrentPriceLabel: 'Aktueller Preis',
    persistError: 'Die Konfiguration konnte gerade nicht gespeichert werden. Bitte erneut versuchen.'
  },
  wizardI18n: {
    choose_option: 'Bitte wählen Sie eine Option.',
    back: 'Zurück'
  }
};

let elements = document.querySelectorAll('[data-react-component=CheckoutProjectLicenseConfigurator]');

elements.forEach(function(element) {
  try {
    let reactProps = parseCheckoutProjectLicenseConfiguratorProps(element.dataset.reactProps);

    ReactDOM.render(
      <React.StrictMode>
        <CheckoutProjectLicenseConfigurator {...reactProps} />
      </React.StrictMode>,
      element
    );
  } catch (error) {
    console.error('Failed to mount CheckoutProjectLicenseConfigurator', error);
  }
});

export default CheckoutProjectLicenseConfigurator;
